import { useCallback, useEffect, useRef, useState } from "react";
import { getWorkingHoursStatus } from "../../resources/api";
import { pauseAll, pauseAllVideos } from "../../utils/audios";
import own from "./SessionEnding.module.css";

const RECHECK_MS = 5 * 60 * 1000; 

/**
 * Watches the student's working hours while they're signed in. For the last few minutes
 * (warnMs from the backend) a "closes soon" bar counts down without blocking anything, so
 * a student can finish a passage or checkpoint; when the hours end, onLock(notice) locks
 * the app with the "learning hours" screen. No working hours, or the check failing,
 * changes nothing.
 *
 *
 * @param {{ onLock: (notice: object) => void }} props
 */
export default function SessionEnding({ onLock }) {
  const deadline = useRef(null); 
  const [secondsLeft, setSecondsLeft] = useState(null); 

  const check = useCallback(async () => {
    const status = await getWorkingHoursStatus();
    if (!status) return; 
    if (status.closed) return onLock(status.notice);
    deadline.current = status.restricted
      ? { at: Date.now() + status.closesInMs, warnMs: status.warnMs, notice: status.notice }
      : null;
    if (!deadline.current) setSecondsLeft(null);
  }, [onLock]);

  useEffect(() => {
    check();
    const recheck = setInterval(check, RECHECK_MS);
    const onVisible = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(recheck);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check]);

  useEffect(() => {
    const tick = setInterval(() => {
      const d = deadline.current;
      if (!d) return;
      const left = d.at - Date.now();
      if (left <= 0) {
        clearInterval(tick);
        pauseAll();
        pauseAllVideos();
        onLock(d.notice);
      } else {
        setSecondsLeft(left <= d.warnMs ? Math.ceil(left / 1000) : null);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [onLock]);

  if (secondsLeft === null) return null;

  const clock = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`;
  return (
    <div className={own.bar} role="status" aria-live="polite">
      <span aria-hidden>⏰</span> Read 21 closes in <b className={own.clock}>{clock}</b>. Finish what you're
      working on.
    </div>
  );
}
